/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Install to Agent Service
 * 
 * API client for installing MCP servers and Skills to coding agents (Cursor, Claude Code).
 * Provides functions for:
 * - Installing MCP servers to agent config files
 * - Installing Skills to agent skills directories
 * - Checking installation status
 * - Getting manual installation instructions
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type { ProductCodingAgent } from '@shared/types/product-app-settings';

// ============================================================================
// Types
// ============================================================================

/** Agent types that support installation */
export type InstallableAgent = 'cursor' | 'claude-code' | 'codex' | 'antigravity';

/** Result of an installation operation */
export interface InstallResult {
  success: boolean;
  serverId?: string;
  serverName?: string;
  skillPath?: string;
  skillName?: string;
  skillDisplayName?: string;
  agent: InstallableAgent;
  agentDisplayName: string;
  configPath?: string;
  installedPath?: string;
  alreadyInstalled: boolean;
  message: string;
}

/** Installation status check result */
export interface InstallationStatus {
  serverId?: string;
  skillPath?: string;
  skillName?: string;
  agent: InstallableAgent;
  installed: boolean;
  configPath?: string;
  installPath?: string;
}

/** Config snippet for manual installation */
export interface ConfigSnippet {
  serverId: string;
  serverName: string;
  agent: InstallableAgent;
  agentDisplayName: string;
  configPath: string;
  snippet: string;
  instructions: string;
}

/** Manual install instructions for skills */
export interface SkillInstallInstructions {
  skillPath: string;
  skillName: string;
  skillDisplayName: string;
  agent: InstallableAgent;
  agentDisplayName: string;
  sourceDir: string;
  targetDir: string;
  instructions: string;
  files: Array<{ name: string; relativePath: string; type: string }>;
}

// ============================================================================
// API Endpoints
// ============================================================================

export const INSTALL_ENDPOINTS = {
  // MCP Server installation
  installMcpServer: '/api/mcp-servers/install',
  mcpServerInstalled: (id: string, agent: string) => `/api/mcp-servers/${id}/installed/${agent}`,
  mcpConfigSnippet: (id: string, agent: string) => `/api/mcp-servers/${id}/config-snippet/${agent}`,
  
  // Skills installation
  installSkill: '/api/vps-skills/install',
  skillInstalled: (path: string, agent: string) => `/api/vps-skills/${encodeURIComponent(path)}/installed/${agent}`,
  skillInstallInstructions: (path: string, agent: string) => `/api/vps-skills/${encodeURIComponent(path)}/install-instructions/${agent}`,
} as const;

// ============================================================================
// Query Keys
// ============================================================================

export const INSTALL_QUERY_KEYS = {
  mcpInstalled: (id: string, agent: string) => ['install', 'mcp', id, agent] as const,
  skillInstalled: (path: string, agent: string) => ['install', 'skill', path, agent] as const,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/** All agent IDs that support MCP installation */
export const INSTALLABLE_AGENT_IDS: readonly InstallableAgent[] = ['cursor', 'claude-code', 'codex', 'antigravity'];

/**
 * Check if a string is a valid InstallableAgent
 */
export function isInstallableAgentId(agent: string | null | undefined): agent is InstallableAgent {
  return agent === 'cursor' || agent === 'claude-code' || agent === 'codex' || agent === 'antigravity';
}

/**
 * Check if a ProductCodingAgent supports installation
 * Note: Not all ProductCodingAgents support MCP installation (e.g., gemini doesn't)
 */
export function isInstallableAgent(agent: ProductCodingAgent | null | undefined): agent is 'cursor' | 'claude-code' {
  return agent === 'cursor' || agent === 'claude-code';
}

/**
 * Convert ProductCodingAgent to InstallableAgent (for API calls)
 * Returns null if the agent doesn't support installation
 */
export function toInstallableAgent(agent: ProductCodingAgent | null | undefined): InstallableAgent | null {
  if (agent === 'cursor' || agent === 'claude-code') {
    return agent;
  }
  return null;
}

/**
 * Get display name for an agent
 */
export function getAgentDisplayName(agent: InstallableAgent): string {
  switch (agent) {
    case 'cursor':
      return 'Cursor';
    case 'claude-code':
      return 'Claude Code';
    case 'codex':
      return 'OpenAI Codex';
    case 'antigravity':
      return 'AntiGravity';
    default:
      return agent;
  }
}

// ============================================================================
// MCP Server Installation
// ============================================================================

/**
 * Install an MCP server to the user's coding agent config
 */
export async function installMcpServer(
  serverId: string,
  agent: InstallableAgent,
  overwrite = false
): Promise<InstallResult> {
  const response = await authenticatedFetch(INSTALL_ENDPOINTS.installMcpServer, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serverId, agent, overwrite }),
  });
  
  return handleApiResponse<InstallResult>(response);
}

/**
 * Check if an MCP server is installed for an agent
 */
export async function isMcpServerInstalled(
  serverId: string,
  agent: InstallableAgent
): Promise<InstallationStatus> {
  const response = await authenticatedFetch(
    INSTALL_ENDPOINTS.mcpServerInstalled(serverId, agent)
  );
  
  return handleApiResponse<InstallationStatus>(response);
}

/**
 * Get config snippet for manual MCP server installation
 */
export async function getMcpConfigSnippet(
  serverId: string,
  agent: InstallableAgent
): Promise<ConfigSnippet> {
  const response = await authenticatedFetch(
    INSTALL_ENDPOINTS.mcpConfigSnippet(serverId, agent)
  );
  
  return handleApiResponse<ConfigSnippet>(response);
}

// ============================================================================
// Skill Installation
// ============================================================================

/**
 * Install a skill to the user's coding agent skills directory
 */
export async function installSkill(
  skillPath: string,
  agent: InstallableAgent,
  overwrite = false
): Promise<InstallResult> {
  const response = await authenticatedFetch(INSTALL_ENDPOINTS.installSkill, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skillPath, agent, overwrite }),
  });
  
  return handleApiResponse<InstallResult>(response);
}

/**
 * Check if a skill is installed for an agent
 */
export async function isSkillInstalled(
  skillPath: string,
  agent: InstallableAgent
): Promise<InstallationStatus> {
  const response = await authenticatedFetch(
    INSTALL_ENDPOINTS.skillInstalled(skillPath, agent)
  );
  
  return handleApiResponse<InstallationStatus>(response);
}

/**
 * Get manual installation instructions for a skill
 */
export async function getSkillInstallInstructions(
  skillPath: string,
  agent: InstallableAgent
): Promise<SkillInstallInstructions> {
  const response = await authenticatedFetch(
    INSTALL_ENDPOINTS.skillInstallInstructions(skillPath, agent)
  );
  
  return handleApiResponse<SkillInstallInstructions>(response);
}
